import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ShieldCheck, ChevronRight } from "lucide-react";
import { useContent } from "../i18n/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const c = useContent();
  const p = c.portal;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const inputCls =
    "w-full rounded-md border border-[#e7e2d8] bg-white pl-11 pr-4 py-3 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition";

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error(p.resetNoToken); return; }
    if (next.length < 6) { toast.error(p.errPassword); return; }
    if (next !== confirm) { toast.error(p.errPwMismatch); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: next });
      toast.success(p.resetOk);
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d === "expired_token" ? p.errExpiredToken : d === "invalid_token" ? p.errInvalidToken : p.errGeneric);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#eee]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#16233d] flex items-center justify-center">
              <span className="font-serif-lux text-2xl text-[#b08d57] leading-none">B</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-lux text-xl text-[#16233d] font-semibold">{c.nav.brand}</div>
              <div className="text-[10px] tracking-[0.2em] text-[#8f6f3f] uppercase">SL · Luxembourg</div>
            </div>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-[#faf8f4] px-6 py-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-[#efe9dd] p-8 shadow-[0_30px_60px_-40px_rgba(22,35,61,0.3)]">
            <div className="w-12 h-12 rounded-lg bg-[#faf3e8] flex items-center justify-center mb-5">
              <ShieldCheck size={24} className="text-[#b08d57]" />
            </div>
            <h1 className="font-serif-lux text-3xl text-[#16233d] font-semibold" data-testid="reset-title">{p.resetTitle}</h1>
            <p className="mt-2 text-[#5c6577] text-sm">{p.resetSubtitle}</p>

            {!token ? (
              <p className="mt-6 text-sm text-red-600" data-testid="reset-no-token">{p.resetNoToken}</p>
            ) : (
              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-form">
                <div className="relative">
                  <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
                  <input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={p.newPwLabel} className={inputCls} data-testid="reset-new-password-input" />
                </div>
                <div className="relative">
                  <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
                  <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={p.confirmPwLabel} className={inputCls} data-testid="reset-confirm-password-input" />
                </div>
                <button type="submit" disabled={busy} className="btn-gold rounded-md w-full py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit-button">
                  {p.resetBtn} <ChevronRight size={17} />
                </button>
              </form>
            )}

            <div className="mt-5 text-center text-sm">
              <Link to="/login" className="font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="reset-back-to-login">
                {p.backToLogin}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
